/**
 * Service de cache centralisé pour optimiser les requêtes API
 * Permet de réduire les appels redondants et améliorer les performances
 */

import { eventBus } from '@/utils/eventBus'

class CacheService {
  constructor() {
    this.cache = new Map()
    this.cacheTimestamps = new Map()
    this.cacheDurations = {
      userProfile: 5 * 60 * 1000, // 5 minutes
      badges: 2 * 60 * 1000, // 2 minutes
      tutorialProgress: 5 * 60 * 1000, // 5 minutes
      gamesProgress: 1 * 60 * 1000, // 1 minute
      pendingRequests: 30 * 1000, // 30 secondes
    }

    // Écouter les événements pour invalider le cache
    this.setupEventListeners()
  }

  setupEventListeners() {
    // Invalider le cache des badges quand un badge est débloqué
    eventBus.on('badge-unlocked', () => {
      this.invalidate('badges')
      this.invalidate('gamesProgress')
    })

    // Invalider le cache du profil quand il est mis à jour
    eventBus.on('profile-updated', () => {
      this.invalidate('userProfile')
    })

    // Invalider le cache tutorial quand la progression change
    eventBus.on('tutorial-progress-updated', () => {
      this.invalidate('tutorialProgress')
    })

    // Invalider tout le cache à la déconnexion
    eventBus.on('user-logged-out', () => {
      this.clearAll()
    })
  }

  /**
   * Récupère une valeur du cache si elle existe et est valide
   * @param {string} key - Clé du cache
   * @returns {any|null} - Valeur en cache ou null si expiré/inexistant
   */
  get(key) {
    const timestamp = this.cacheTimestamps.get(key)
    const duration = this.cacheDurations[key] || 60000 // 1 minute par défaut

    if (!timestamp || Date.now() - timestamp > duration) {
      // Cache expiré ou inexistant
      this.cache.delete(key)
      this.cacheTimestamps.delete(key)
      return null
    }

    return this.cache.get(key)
  }

  /**
   * Stocke une valeur dans le cache
   * @param {string} key - Clé du cache
   * @param {any} value - Valeur à stocker
   */
  set(key, value) {
    this.cache.set(key, value)
    this.cacheTimestamps.set(key, Date.now())

    // Sauvegarder aussi dans localStorage pour persistance entre rafraîchissements
    try {
      localStorage.setItem(`cache_${key}`, JSON.stringify({
        value,
        timestamp: Date.now()
      }))
    } catch (error) {
      console.warn('Erreur lors de la sauvegarde du cache dans localStorage:', error)
    }
  }

  /**
   * Récupère une valeur du cache ou exécute la fonction de fetch
   * @param {string} key - Clé du cache
   * @param {Function} fetchFn - Fonction async pour récupérer les données
   * @returns {Promise<any>} - Données en cache ou récupérées
   */
  async getOrFetch(key, fetchFn) {
    // Vérifier d'abord le cache en mémoire
    let cached = this.get(key)
    if (cached !== null) {
      return cached
    }

    // Vérifier localStorage en fallback
    try {
      const stored = localStorage.getItem(`cache_${key}`)
      if (stored) {
        const { value, timestamp } = JSON.parse(stored)
        const duration = this.cacheDurations[key] || 60000

        if (Date.now() - timestamp < duration) {
          this.cache.set(key, value)
          this.cacheTimestamps.set(key, timestamp)
          return value
        }
      }
    } catch (error) {
      console.warn('Erreur lors de la lecture du cache localStorage:', error)
    }
    const data = await fetchFn()
    this.set(key, data)
    return data
  }

  /**
   * Invalide une clé spécifique du cache
   * @param {string} key - Clé à invalider
   */
  invalidate(key) {
    this.cache.delete(key)
    this.cacheTimestamps.delete(key)
    try {
      localStorage.removeItem(`cache_${key}`)
    } catch (error) {
      console.warn('Erreur lors de la suppression du cache localStorage:', error)
    }
  }

  /**
   * Invalide plusieurs clés
   * @param {string[]} keys - Tableau de clés à invalider
   */
  invalidateMultiple(keys) {
    keys.forEach(key => this.invalidate(key))
  }

  /**
   * Vide tout le cache
   */
  clearAll() {
    this.cache.clear()
    this.cacheTimestamps.clear()

    // Nettoyer localStorage
    try {
      const keysToRemove = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith('cache_')) {
          keysToRemove.push(key)
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key))
    } catch (error) {
      console.warn('Erreur lors du nettoyage du cache localStorage:', error)
    }
  }

  /**
   * Configure la durée du cache pour une clé
   * @param {string} key - Clé du cache
   * @param {number} duration - Durée en millisecondes
   */
  setCacheDuration(key, duration) {
    this.cacheDurations[key] = duration
  }
}

// Export une instance singleton
export default new CacheService()
